// VideoLesson.tsx — a video step. Uses a native <video> element configured for
// iOS/Android webviews: playsInline (no forced fullscreen), no autoplay, native
// accessible controls, metadata preload, and resume-from-last-position.
import React from 'react';
import { Display, Eyebrow, Body, Meta, Pressable, Plate, fmt } from '@/components/ui';
import { Icon } from '@/components/icons';
import { CompletionButton } from '@/components/CompletionButton';
import { MeditationNote, isMeditation } from '@/components/MeditationNote';
import { FavouriteButton } from '@/components/FavouriteButton';
import type { Module, Step } from '@/types';
import { moduleName } from '@/services/contentProvider';
import { VIDEO_IS_PLACEHOLDER } from '@/data/video-sources';
import { useProgress } from '@/hooks/useProgress';
import { Capacitor } from '@capacitor/core';

// Dark circular button used for the web video toolbar (above the player).
const webBackStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 999,
  border: '1px solid rgba(246,224,190,0.16)',
  background: 'rgba(14,10,6,0.55)',
  color: '#F6E6C5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const fontBtnStyle: React.CSSProperties = {
  minWidth: 30,
  height: 30,
  borderRadius: 999,
  border: 'none',
  background: 'transparent',
  color: 'var(--ink)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'Fraunces, Georgia, serif',
};

export function VideoLesson({
  step,
  module,
  chapterLabel,
  author,
  initialPosition,
  isComplete,
  onPositionChange,
  onComplete,
  onOpenReflection,
  onClose,
}: {
  step: Step;
  module: Module;
  /** Pre-composed locator, e.g. "Self-Compassion & Self-Worth · Chapter 2". */
  chapterLabel: string;
  author?: { name: string; role: string };
  initialPosition: number;
  isComplete: boolean;
  onPositionChange: (seconds: number) => void;
  onComplete: () => void;
  /** Open the optional reflection for this video, if it has one. */
  onOpenReflection?: () => void;
  onClose: () => void;
}) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [showText, setShowText] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  // A transient media error must not permanently dead-end the player — desktop web
  // is prone to a one-off stall, and the old code swapped the <video> out for the
  // error state with no way back. Auto-retry the load a couple of times (remounting
  // the element via reloadKey) and offer a manual "Try again" before giving up.
  const [reloadKey, setReloadKey] = React.useState(0);
  const retriesRef = React.useRef(0);
  const onVideoError = () => {
    if (retriesRef.current < 2) {
      retriesRef.current += 1;
      setReloadKey((k) => k + 1);
    } else {
      setFailed(true);
    }
  };
  const retryVideo = () => {
    retriesRef.current = 0;
    setFailed(false);
    setReloadKey((k) => k + 1);
  };
  const progress = useProgress();
  const fontScale = progress.state.fontScale;
  const hasVideo = !!step.video_url;
  // On the web the browser's own <video> controls handle volume/fullscreen/PiP,
  // so the app's floating buttons must sit ABOVE the player, not over it.
  const isWeb = !Capacitor.isNativePlatform();

  // Resume from the saved position once metadata is known, then start playing
  // automatically — the lesson was opened deliberately, so begin on arrival.
  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (initialPosition > 0 && initialPosition < (step.duration ?? Infinity)) {
      try {
        v.currentTime = initialPosition;
      } catch {
        /* some webviews disallow seeking before play; ignore */
      }
    }
    // Best-effort autoplay. If the OS blocks playback with sound, fall back to
    // muted autoplay (still better than a frozen poster); the user can unmute
    // via the native controls.
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {
        /* fully blocked — leave paused with controls showing */
      });
    });
  };

  // Persist position periodically and on unmount.
  const lastSaved = React.useRef(0);
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (Math.abs(v.currentTime - lastSaved.current) >= 3) {
      lastSaved.current = v.currentTime;
      onPositionChange(v.currentTime);
    }
  };
  React.useEffect(() => {
    return () => {
      const v = videoRef.current;
      if (v && v.currentTime > 0) onPositionChange(v.currentTime);
    };
  }, [onPositionChange]);

  // YouTube-style fullscreen. On iOS the native player (webkitEnterFullscreen)
  // rotates to landscape on its own — even while the app itself stays portrait.
  // Elsewhere we use the standard Fullscreen API on the video element.
  const enterFullscreen = () => {
    const v = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    if (!v) return;
    if (typeof v.webkitEnterFullscreen === 'function') v.webkitEnterFullscreen();
    else if (typeof v.requestFullscreen === 'function') v.requestFullscreen().catch(() => {});
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 82,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        {/* App chrome (back, full-screen, favourite) sits in a toolbar ABOVE the
            player — never over the picture or the video's own controls. On native
            this also clears the notch via the safe-area inset. Fixes the portrait
            clash where these buttons overlapped the video. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            paddingTop: isWeb ? 10 : 'calc(var(--safe-top) + 10px)',
            background: '#120c06',
          }}
        >
          <Pressable as="button" onClick={onClose} ariaLabel="Close lesson" style={webBackStyle}>
            <Icon name="chevDown" size={20} />
          </Pressable>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {!isWeb && hasVideo && !failed && (
              <Pressable as="button" onClick={enterFullscreen} ariaLabel="Full screen" style={webBackStyle}>
                <Icon name="expand" size={18} />
              </Pressable>
            )}
            <FavouriteButton stepId={step.id} onLight />
          </div>
        </div>
        {/* video / poster */}
        <div style={{ position: 'relative', background: '#120c06' }}>
          {hasVideo && !failed ? (
            <video
              ref={videoRef}
              key={reloadKey}
              src={step.video_url}
              poster={step.poster_url}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              onError={onVideoError}
              style={{ width: '100%', maxHeight: '46vh', display: 'block', background: '#120c06' }}
            />
          ) : (
            <Plate
              ratio="16 / 11"
              caption="lesson still · evening light"
              glow="42% 36%"
              poster={step.poster_url}
              overlay={
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    textAlign: 'center',
                    padding: 24,
                    color: '#F6E6C5',
                  }}
                >
                  <Icon name="film" size={30} />
                  <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, opacity: 0.8 }}>
                    {failed ? 'This video could not load right now.' : 'Video will be available here soon.'}
                  </span>
                  {failed && (
                    <Pressable
                      as="button"
                      onClick={retryVideo}
                      ariaLabel="Try loading the video again"
                      style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 999, border: '1px solid rgba(246,224,190,0.3)', background: 'rgba(246,224,190,0.12)', color: '#F6E6C5', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <Icon name="refresh" size={15} /> Try again
                    </Pressable>
                  )}
                </div>
              }
            />
          )}
        </div>

        <div style={{ padding: '22px 24px calc(36px + var(--safe-bottom))' }}>
          <Eyebrow color="var(--amber-deep)">
            {chapterLabel} · {moduleName(module)}
          </Eyebrow>
          <div style={{ marginTop: 9 }}>
            <Display parts={step.title} size={23} lh={1.16} />
          </div>
          {step.duration != null && (
            <Meta style={{ display: 'block', marginTop: 8 }}>
              {fmt(step.duration)}
              {VIDEO_IS_PLACEHOLDER ? ' · preview clip' : ''}
            </Meta>
          )}

          {/* attribution */}
          {author && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 18 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  background: 'linear-gradient(140deg,#E1A943,#9B6712)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FBF1DE',
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 16,
                }}
              >
                cn
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 600 }}>
                  {author.name}
                </div>
                <Meta style={{ display: 'block' }}>{author.role}</Meta>
              </div>
            </div>
          )}

          {step.blurb && (
            <Body size={15.5} style={{ marginTop: 18 }}>
              {step.blurb}
            </Body>
          )}

          {step.video_reflection && onOpenReflection && (
            <div style={{ marginTop: 24 }}>
              {/* a quiet cue that this is a slow, reflective programme — set the
                  tempo right where it's easy to rush past to the next lesson */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 7,
                  marginBottom: 11,
                  color: 'var(--amber-deep)',
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 14.5,
                  lineHeight: 1.45,
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 1 }}>
                  <Icon name="leaf" size={15} />
                </span>
                <span>No rush — this programme works best slowly. Take a few minutes with this before moving on.</span>
              </div>
              <Pressable
                as="button"
                onClick={onOpenReflection}
                ariaLabel="Open the reflection for this video"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '15px 16px 16px',
                  borderRadius: 18,
                  border: '1.5px solid rgba(29,158,117,0.42)',
                  background: 'linear-gradient(180deg, rgba(29,158,117,0.11), rgba(29,158,117,0.045))',
                  boxShadow: 'var(--sh1)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 999,
                      background: 'var(--green-deep)',
                      color: '#FBF1DE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="pencil" size={17} />
                  </span>
                  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 18.5, fontWeight: 500, color: 'var(--ink)' }}>
                    Take a moment
                  </span>
                  <span style={{ flex: 1 }} />
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      color: 'var(--green-deep)',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 13.5,
                      fontWeight: 700,
                    }}
                  >
                    Reflect <Icon name="chevRight" size={15} />
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: 'var(--ink-soft)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {step.video_reflection.split('\n').map((l) => l.trim()).filter(Boolean)[0]}
                </div>
              </Pressable>
            </div>
          )}

          {isMeditation(step.title) && <MeditationNote />}

          <div style={{ marginTop: 26 }}>
            <CompletionButton
              full
              done={isComplete}
              onComplete={onComplete}
              onDone={onClose}
              label="Mark lesson complete"
              doneLabel="Completed · tap to close"
            />
          </div>

          {/* the spoken transcript — open by default; the A−/A+ control sets the
              app-wide reading text size (persisted), so it carries to lessons too */}
          {step.transcript && step.transcript.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginTop: 22,
                }}
              >
                <Pressable
                  as="button"
                  onClick={() => setShowText((s) => !s)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'none',
                    border: 'none',
                    color: 'var(--ink-soft)',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13.5,
                    padding: 0,
                    minHeight: 44,
                  }}
                  ariaLabel="Toggle transcript"
                >
                  <Icon name={showText ? 'chevDown' : 'chevRight'} size={16} /> Transcript
                </Pressable>
                {/* font-size control — sets the app's default reading size */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: 3,
                    borderRadius: 999,
                    border: '1px solid var(--hair)',
                    background: 'var(--card-top)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => progress.setFontScale(fontScale - 0.1)}
                    aria-label="Smaller text"
                    style={fontBtnStyle}
                  >
                    <span style={{ fontSize: 12 }}>A</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => progress.setFontScale(1)}
                    aria-label="Reset text size"
                    style={{ ...fontBtnStyle, width: 'auto', padding: '0 8px', fontSize: 10, fontWeight: 600 }}
                  >
                    {Math.round(fontScale * 100)}%
                  </button>
                  <button
                    type="button"
                    onClick={() => progress.setFontScale(fontScale + 0.1)}
                    aria-label="Larger text"
                    style={fontBtnStyle}
                  >
                    <span style={{ fontSize: 18 }}>A</span>
                  </button>
                </div>
              </div>
              {showText && (
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {step.transcript.map((t, i) => (
                    <Body key={i} dim size={Math.round(14.5 * fontScale * 10) / 10}>
                      {t}
                    </Body>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
